/**
 * Call History Search Repository Implementation (Infrastructure)
 *
 * Implements the domain repository interface for call history search operations.
 */

import { AppException, ErrorCode, NotFoundException } from '@/core/exceptions';

import type { CallHistorySearchRepository } from '../../domain/repositories/callHistorySearchRepository';
import type { CallHistorySearchRequest } from '../../domain/types/requests';
import type { CallHistorySearchResponse, CallHistorySearchOptionsResponse } from '../../domain/types/responses';
import type { CallHistorySearchDatasource } from '../datasources/callHistorySearchDatasource';
import { callHistorySearchDatasource } from '../datasources/callHistorySearchDatasource';

// ============================================================================
// Repository Implementation
// ============================================================================

export class CallHistorySearchRepositoryImpl implements CallHistorySearchRepository {
  constructor(private readonly datasource: CallHistorySearchDatasource = callHistorySearchDatasource) {}

  async searchCallHistory(request: CallHistorySearchRequest): Promise<CallHistorySearchResponse> {
    try {
      return await this.datasource.searchCallHistory(request);
    } catch (error) {
      if (error instanceof NotFoundException) {
        return { items: [], total: 0, page: request.page, per_page: request.per_page };
      }
      throw this.handleError(error, 'Failed to search call history');
    }
  }

  async getSearchOptions(): Promise<CallHistorySearchOptionsResponse> {
    try {
      return await this.datasource.getSearchOptions();
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch call history search options');
    }
  }

  private handleError(error: unknown, fallbackMessage: string): AppException {
    if (error instanceof AppException) {
      return error;
    }
    const message = error instanceof Error ? error.message : fallbackMessage;
    return new AppException({
      code: ErrorCode.UNKNOWN_ERROR,
      message,
      userMessage: fallbackMessage,
    });
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const callHistorySearchRepository: CallHistorySearchRepository = new CallHistorySearchRepositoryImpl();
